import { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import "@/App.css";
import { 
  Shield, Lock, Code, Cloud, Users, GraduationCap, ClipboardCheck,
  ChevronRight, Menu, X, Phone, Mail, MapPin, Linkedin, Twitter,
  CheckCircle, Star, ArrowRight, ExternalLink, Award, Target, Eye,
  FileText, AlertTriangle, Server, Globe, Zap, TrendingUp, ChevronUp,
  Building, UserCheck, ShieldCheck, Activity
} from "lucide-react";

// ============ STATIC DATA ============

// Services Data
const SERVICES_DATA = [
  {
    id: "vapt",
    title: "VAPT Services",
    shortTitle: "VAPT",
    description: "Comprehensive Vulnerability Assessment and Penetration Testing to identify and exploit security weaknesses before attackers do.",
    features: ["Network Penetration Testing", "Web Application Testing", "Mobile App Security", "API Security Assessment", "Social Engineering Tests"],
    icon: "shield"
  },
  {
    id: "appsec",
    title: "Application Security",
    shortTitle: "AppSec",
    description: "End-to-end application security services including secure code review, SAST/DAST, and DevSecOps integration.",
    features: ["Secure Code Review", "SAST & DAST Integration", "DevSecOps Implementation", "Security Architecture Review", "Threat Modeling"],
    icon: "code"
  },
  {
    id: "grc",
    title: "GRC Services",
    shortTitle: "GRC",
    description: "Governance, Risk, and Compliance services to help you meet regulatory requirements and industry standards.",
    features: ["ISO 27001 Implementation", "SOC 2 Compliance", "GDPR/DPDPA Compliance", "Risk Assessment", "Policy Development"],
    icon: "clipboard"
  },
  {
    id: "vciso",
    title: "Virtual CISO",
    shortTitle: "vCISO",
    description: "Strategic security leadership without the full-time cost. Our vCISO services provide expert guidance for your security program.",
    features: ["Security Strategy Development", "Board-Level Reporting", "Security Program Management", "Vendor Risk Management", "Incident Response Planning"],
    icon: "user"
  },
  {
    id: "cloud",
    title: "Cloud Security",
    shortTitle: "Cloud",
    description: "Secure your cloud infrastructure across AWS, Azure, and GCP with our comprehensive cloud security assessments.",
    features: ["Cloud Configuration Review", "IAM Assessment", "Container Security", "Kubernetes Security", "Cloud Compliance"],
    icon: "cloud"
  },
  {
    id: "mss",
    title: "Managed Security Services",
    shortTitle: "MSS",
    description: "24/7 security operations and monitoring to detect, respond, and remediate threats in real-time.",
    features: ["24/7 Security Monitoring", "Threat Detection & Response", "SIEM Management", "Vulnerability Management", "Incident Response"],
    icon: "shield"
  },
  {
    id: "privacy",
    title: "Privacy Services",
    shortTitle: "Privacy",
    description: "Comprehensive privacy consulting to help you comply with global data protection regulations and build customer trust.",
    features: ["GDPR Compliance", "DPDPA Implementation", "Privacy Impact Assessments", "Data Mapping & Classification", "Privacy Policy Development"],
    icon: "lock"
  }
];

// Stats Data
const STATS_DATA = [
  { value: "2", label: "Expert Partners" },
  { value: "40+", label: "Assessments Conducted" },
  { value: "100%", label: "Client Satisfaction" },
  { value: "40+", label: "Years of Industry Experience" },
  { value: "10+", label: "Solutions Partners" }
];

// Clients Data
const CLIENTS_DATA = [
  { id: "1", name: "Uni Cards", logo: "https://customer-assets.emergentagent.com/job_e8966f76-de09-4ceb-b586-4acb792319d0/artifacts/h8nfxs8q_400x400ia-75.webp" },
  { id: "2", name: "Bootlabs", logo: "https://customer-assets.emergentagent.com/job_5edc397f-12d9-4bf5-9bb6-c4b93ff5263c/artifacts/npyz9bi2_bootslab-logo-white.png" },
  { id: "3", name: "EFR", logo: "https://customer-assets.emergentagent.com/job_5edc397f-12d9-4bf5-9bb6-c4b93ff5263c/artifacts/jjs7nk6h_ef-r-logo.png" },
  { id: "4", name: "Signzy", logo: "https://customer-assets.emergentagent.com/job_5edc397f-12d9-4bf5-9bb6-c4b93ff5263c/artifacts/ua0nixna_Logo.0740c233.svg" },
  { id: "5", name: "Jupiter Money", logo: "https://customer-assets.emergentagent.com/job_5edc397f-12d9-4bf5-9bb6-c4b93ff5263c/artifacts/0sul3si5_jupiter-logo-white.svg" },
  { id: "6", name: "Reward360", logo: "https://customer-assets.emergentagent.com/job_5edc397f-12d9-4bf5-9bb6-c4b93ff5263c/artifacts/zx0vrgte_5fa95c11dcae6afc356121f7_R360_unit-inverted.svg" }
];

// Case Studies Data
const CASE_STUDIES_DATA = [
  {
    id: "1",
    title: "Banking Security Transformation",
    industry: "Financial Services",
    challenge: "A major private bank needed to comply with RBI cybersecurity guidelines while modernizing their legacy infrastructure.",
    solution: "Comprehensive security assessment, gap analysis, and implementation of security controls aligned with RBI frameworks.",
    results: ["100% RBI compliance achieved", "70% reduction in security incidents", "Zero data breaches post-implementation"]
  },
  {
    id: "2",
    title: "Online Lending Platform Security",
    industry: "FinTech",
    challenge: "High risk of data exposure and security vulnerabilities threatening financial assets and customer trust.",
    solution: "Conducted a comprehensive security audit, identifying critical issues like privacy controls, credential mismanagement, and insecure data storage.",
    results: ["Strengthened platform security", "Protected financial assets", "Enhanced reputation and customer trust"]
  },
  {
    id: "3",
    title: "Government Institution SDLC Transformation",
    industry: "Government",
    challenge: "Complex software development life cycle with high manual effort and security gaps.",
    solution: "Implemented a Secure SDLC process with automated CI/CD pipelines, triggering security actions across multiple stages.",
    results: ["Improved security posture", "Early threat detection", "Significant time savings for SDLC team"]
  }
];

// ============ END STATIC DATA ============

// Icon mapping
const iconMap = {
  shield: Shield,
  code: Code,
  clipboard: ClipboardCheck,
  user: Users,
  cloud: Cloud,
  graduation: GraduationCap,
  lock: Lock,
};

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
          const suffix = value.replace(/[0-9]/g, '');
          
          let start = 0;
          const end = numericValue;
          const increment = end / (duration / 16);
          
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, duration, hasAnimated]);

  const suffix = value.replace(/[0-9]/g, '');
  return <span ref={ref}>{count}{suffix}</span>;
};

// Scroll Animation Hook
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Privacy Policy Modal Component
const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="privacy-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      
      {/* Modal Content */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-8 py-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">HTS Consulting Privacy Policy</h2>
          <button 
            onClick={onClose}
            className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-8 py-6 overflow-y-auto max-h-[calc(90vh-100px)] text-gray-300 space-y-6">
          <p className="text-sm text-gray-400">Effective Date: 22nd July 2024</p>
          
          {/* Introduction */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Introduction</h3>
            <p className="text-sm leading-relaxed">
              At HTS Consulting, we are committed to protecting your privacy and ensuring the security of your information. As a cybersecurity consulting firm, we understand the sensitivity of the data we handle and prioritize the confidentiality and integrity of your information. This Privacy Policy outlines our practices regarding the collection, use, and protection of your personal and business data.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Information We Collect</h3>
            <p className="text-sm mb-3">We may collect the following types of information in connection with our services:</p>
            <ul className="space-y-2 text-sm">
              <li><strong className="text-white">Personal Information:</strong> This includes names, email addresses, phone numbers, and any other contact details you provide when you engage with us.</li>
              <li><strong className="text-white">Business Information:</strong> Company name, industry, size, and other relevant details necessary for delivering our services.</li>
              <li><strong className="text-white">Technical Data:</strong> Information regarding your systems, networks, and security protocols, which might be collected during assessments or audits.</li>
              <li><strong className="text-white">Cookies and Tracking Technologies:</strong> We may use cookies to improve your experience on our website. You can choose to accept or decline cookies, but this may prevent you from taking full advantage of the website.</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">How We Use Your Information</h3>
            <p className="text-sm mb-3">We use your information for a variety of purposes, including:</p>
            <ul className="space-y-2 text-sm">
              <li><strong className="text-white">Service Delivery:</strong> To provide you with the services you request and to manage your account.</li>
              <li><strong className="text-white">Communication:</strong> To contact you regarding your inquiries, service updates, and other communications relevant to our services.</li>
              <li><strong className="text-white">Improvement of Services:</strong> To analyze data and improve our offerings, ensuring we meet your needs effectively.</li>
              <li><strong className="text-white">Compliance and Security:</strong> To comply with legal obligations and maintain our systems' security.</li>
            </ul>
          </section>

          {/* Disclosure of Your Information */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Disclosure of Your Information</h3>
            <p className="text-sm mb-3">We do not sell or rent your personal information to third parties. We may share your information in the following circumstances:</p>
            <ul className="space-y-2 text-sm">
              <li><strong className="text-white">With Service Providers:</strong> We may engage third-party service providers who assist us in delivering our services. These parties are contractually obligated to protect your information.</li>
              <li><strong className="text-white">Legal Requirements:</strong> We may disclose information if required to do so by law or in response to valid requests by public authorities.</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Data Security</h3>
            <p className="text-sm leading-relaxed">
              We implement appropriate technical and organizational measures to protect your data from unauthorized access, loss, or disclosure. This includes using encryption, secure servers, and regular security assessments.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Your Rights</h3>
            <p className="text-sm mb-3">You have the right to:</p>
            <ul className="space-y-2 text-sm">
              <li><strong className="text-white">Access Your Information:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong className="text-white">Rectification:</strong> Request correction of any inaccurate or incomplete data.</li>
              <li><strong className="text-white">Erasure:</strong> Request deletion of your personal data, subject to certain conditions.</li>
              <li><strong className="text-white">Data Portability:</strong> Request transfer of your data to another service provider.</li>
            </ul>
            <p className="text-sm mt-3">To exercise your rights, please contact us using the information provided below.</p>
          </section>

          {/* Changes to This Privacy Policy */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Changes to This Privacy Policy</h3>
            <p className="text-sm leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any significant changes by posting the new policy on our website and updating the effective date.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Contact Us</h3>
            <p className="text-sm leading-relaxed">
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="mt-3 text-sm">
              <p className="text-white font-medium">HTS Consulting</p>
              <p className="text-cyan-400">info@hts.consulting</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// Terms Modal Component
const TermsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="terms-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      
      {/* Modal Content */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-8 py-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">HTS Consulting General Business Terms</h2>
          <button 
            onClick={onClose}
            className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-8 py-6 overflow-y-auto max-h-[calc(90vh-100px)] text-gray-300 space-y-6">
          {/* Definitions */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Definitions</h3>
            <ul className="space-y-2 text-sm">
              <li><strong className="text-white">"Affiliate"</strong> means any entity controlling, controlled by, or under common control with HTS Consulting.</li>
              <li><strong className="text-white">"Claim"</strong> means any claim or legal proceeding of any nature, whether in contract, tort, breach of statutory duty, or otherwise.</li>
              <li><strong className="text-white">"Client"</strong> refers to any entity to which the Engagement Letter is addressed and whose name is reproduced therein.</li>
              <li><strong className="text-white">"Contract"</strong> means the Engagement Letter (EL) and these General Business Terms (GBT).</li>
              <li><strong className="text-white">"Deliverable(s)"</strong> means any advice, reports, work products, or other outputs identified or described in the Engagement Letter and agreed to be provided to the Client as part of the Services.</li>
              <li><strong className="text-white">"Services"</strong> means the consulting services and Deliverables described in the Engagement Letter.</li>
              <li><strong className="text-white">"Subcontractor"</strong> means any third party (other than HTS Consulting) to whom we may subcontract any of the Services.</li>
              <li><strong className="text-white">"You," "your"</strong> refers to the Client; <strong className="text-white">"we," "us," "our," and "HTS Consulting"</strong> refer to Horizontal Thinkers LLP.</li>
            </ul>
          </section>

          {/* About this Contract */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">About this Contract</h3>
            <p className="text-sm leading-relaxed">
              This Contract outlines the terms under which HTS Consulting agrees to provide Services to you. Both parties act as principals, and neither party may bind the other to legal obligations without mutual agreement through a validly executed document.
            </p>
          </section>

          {/* Services */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Services</h3>
            <div className="space-y-3 text-sm leading-relaxed">
              <p>HTS Consulting agrees to provide Services as defined in this Contract. You acknowledge that the scope of the Services is sufficient for your intended purposes.</p>
              <p>We may subcontract Services to other third parties or affiliates. However, your contractual relationship is solely with HTS Consulting and we alone remain responsible and liable under this Contract. You shall not bring any Claim against any Subcontractor or any other entity associated with HTS Consulting.</p>
              <p>You may rely only on our final Deliverable. If you wish to rely on drafts or oral advice, please inform us so we can provide a suitable written final Deliverable.</p>
            </div>
          </section>

          {/* Use and Disclosure of Deliverables */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Use and Disclosure of Deliverables</h3>
            <p className="text-sm leading-relaxed">
              You shall not disclose the Deliverable(s) or any part of them to any third party without our express written consent, except as required by applicable laws. You will use the Deliverable(s) solely for the purposes specified in the Engagement Letter and shall not use them for external advertising or other business decisions without our prior written consent.
            </p>
          </section>

          {/* Your Responsibilities */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Your Responsibilities</h3>
            <p className="text-sm mb-3">To enable us to provide the Services, you agree to:</p>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Cooperate with HTS Consulting by providing timely access to necessary data and information.</li>
              <li>Ensure the accuracy and completeness of all information provided.</li>
              <li>Obtain any required consents for the use of any personal or confidential information.</li>
              <li>Accept responsibility for management decisions and the implementation of our advice.</li>
            </ol>
          </section>

          {/* Our Responsibilities */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Our Responsibilities</h3>
            <p className="text-sm leading-relaxed">
              HTS Consulting warrants to perform the Services in good faith and in a professional manner. We will strive to deliver the Services in accordance with any timelines specified in the Engagement Letter.
            </p>
          </section>

          {/* Acceptance of Deliverables */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Acceptance of Deliverables</h3>
            <p className="text-sm leading-relaxed">
              Unless otherwise specified in the Engagement Letter, any Deliverables will be deemed accepted by you within 10 days of their delivery or when you first utilize them in your business, whichever occurs first.
            </p>
          </section>

          {/* Governing Law and Jurisdiction */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Governing Law and Jurisdiction</h3>
            <p className="text-sm leading-relaxed">
              This Contract, including all matters relating to it, shall be governed by the laws of India without giving effect to the choice of law principles thereof. The parties submit to the exclusive jurisdiction of the courts of law in Chennai and their appeal courts.
            </p>
          </section>

          {/* Use of Name and Logo */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">Use of Name and Logo</h3>
            <p className="text-sm leading-relaxed">
              We may refer to the Services we have performed, to present our experience and credentials along with your name and/or logo for the limited purpose of internal use and/or in proposals to our existing or prospective clients.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Back to Top Button
const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      onClick={scrollToTop}
      className={`fixed bottom-8 right-8 z-50 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white shadow-lg shadow-cyan-500/30 transition-all duration-300 hover:scale-110 hover:shadow-cyan-500/50 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
      }`}
      data-testid="back-to-top"
    >
      <ChevronUp className="w-6 h-6" />
    </button>
  );
};

// Navbar Component
const Navbar = ({ isScrolled, services = [] }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSolutions, setShowSolutions] = useState(false);
  const [showServices, setShowServices] = useState(false);

  const navLinks = [
    { name: "Home", href: "/#home" },
    { name: "About", href: "/#about" },
    { name: "Case Studies", href: "/#case-studies" },
  ];

  // Service icons and colors for dropdown
  const serviceStyles = {
    vapt: { color: "from-red-500 to-orange-500", icon: Shield },
    appsec: { color: "from-purple-500 to-violet-500", icon: Code },
    grc: { color: "from-emerald-500 to-green-500", icon: ClipboardCheck },
    vciso: { color: "from-amber-500 to-yellow-500", icon: Users },
    cloud: { color: "from-sky-500 to-blue-500", icon: Cloud },
    mss: { color: "from-teal-500 to-cyan-500", icon: Shield },
    privacy: { color: "from-pink-500 to-rose-500", icon: Lock },
  };

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/20" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_5edc397f-12d9-4bf5-9bb6-c4b93ff5263c/artifacts/80m017wg_LLP%20White.png" 
              alt="HTS Consulting" 
              className="h-12 object-contain group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="relative text-gray-300 hover:text-cyan-400 transition-colors text-sm font-medium group"
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}

            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setShowServices(true)}
              onMouseLeave={() => setShowServices(false)}
            >
              <button
                className="relative text-gray-300 hover:text-cyan-400 transition-colors text-sm font-medium group flex items-center gap-1"
                data-testid="nav-services"
              >
                Services
                <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${showServices ? 'rotate-90' : ''}`} />
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 group-hover:w-full transition-all duration-300"></span>
              </button>
              
              {/* Services Dropdown Menu */}
              <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 ${showServices ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                <div className="bg-slate-800/95 backdrop-blur-xl border border-slate-700 rounded-xl p-4 min-w-[480px] shadow-2xl shadow-black/30">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {services.map((service) => {
                      return (
                        <Link
                          key={service.id}
                          to={`/services/${service.id}`}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-700/50 transition-all group"
                          data-testid={`nav-service-${service.id}`}
                        >
                          <div>
                            <div className="text-white text-sm font-medium group-hover:text-cyan-400 transition-colors">{service.shortTitle}</div>
                            <div className="text-gray-500 text-xs">{service.title}</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                  <div className="border-t border-slate-700 mt-3 pt-3">
                    <a
                      href="/#services"
                      className="flex items-center justify-center gap-2 p-2 text-cyan-400 text-sm hover:text-cyan-300 transition-colors"
                    >
                      View All Services
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Solutions Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setShowSolutions(true)}
              onMouseLeave={() => setShowSolutions(false)}
            >
              <button
                className="relative text-gray-300 hover:text-cyan-400 transition-colors text-sm font-medium group flex items-center gap-1"
                data-testid="nav-solutions"
              >
                Solutions
                <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${showSolutions ? 'rotate-90' : ''}`} />
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 group-hover:w-full transition-all duration-300"></span>
              </button>
              
              {/* Dropdown Menu */}
              <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 ${showSolutions ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                <div className="bg-slate-800/95 backdrop-blur-xl border border-slate-700 rounded-xl p-4 min-w-[300px] shadow-2xl shadow-black/30">
                  <a
                    href="https://demo.htsone.ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-700/50 transition-all group"
                    data-testid="solution-htsone"
                  >
                    <div>
                      <div className="text-white font-semibold group-hover:text-cyan-400 transition-colors flex items-center gap-2">
                        HTSOne
                        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">NEW</span>
                      </div>
                      <div className="text-gray-400 text-sm">Your AI Security Engineer</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
            
            <a
              href="/#contact"
              className="relative bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-2.5 rounded-lg font-medium overflow-hidden group"
              data-testid="nav-cta"
            >
              <span className="relative z-10">Get Started</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${isMobileMenuOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="bg-slate-800/95 backdrop-blur-xl rounded-2xl mt-2 p-4 border border-slate-700" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block py-3 text-gray-300 hover:text-cyan-400 hover:pl-2 transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            
            {/* Mobile Services */}
            <div className="py-3 border-t border-slate-700 mt-2">
              <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">Services</div>
              <div className="grid grid-cols-2 gap-2">
                {services.map((service) => {
                  return (
                    <Link
                      key={service.id}
                      to={`/services/${service.id}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2 rounded-lg hover:bg-slate-700/50 transition-all"
                    >
                      <span className="text-gray-300 text-sm">{service.shortTitle}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
            
            {/* Mobile Solutions */}
            <div className="py-3 border-t border-slate-700 mt-2">
              <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2">Solutions</div>
              <a
                href="https://demo.htsone.ai/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-700/50 transition-all"
              >
                <div>
                  <div className="text-white font-medium flex items-center gap-2">
                    HTSOne
                    <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">NEW</span>
                  </div>
                  <div className="text-gray-400 text-xs">Your AI Security Engineer</div>
                </div>
              </a>
            </div>
            
            <a
              href="/#contact"
              className="block mt-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg font-medium text-center"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get Started
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Animated Gradient Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse-slow animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow animation-delay-4000"></div>
        
        {/* Cyber Grid */}
        <div className="absolute inset-0 opacity-20">
          <div className="cyber-grid"></div>
        </div>
        
        {/* Floating particles */}
        <div className="particles">
          {[...Array(30)].map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
            }}></div>
          ))}
        </div>
      </div>

      <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-8 animate-float" data-testid="hero-badge">
            <Shield className="w-4 h-4 text-cyan-400 mr-2 animate-pulse" />
            <span className="text-cyan-400 text-sm font-medium">Trusted Cybersecurity Partner</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
            Beyond Defense.
            <br />
            <span className="gradient-text-animate">
              We Elevate Security Standards.
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed" data-testid="hero-subtitle">
            Comprehensive cybersecurity consulting services including VAPT, Application Security, 
            GRC, and Virtual CISO services. Protect your digital assets with India's trusted security experts.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#contact"
              className="group relative bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold overflow-hidden shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300"
              data-testid="hero-cta-primary"
            >
              <span className="relative z-10 flex items-center">
                Schedule Free Consultation
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
            <a
              href="#services"
              className="group border-2 border-gray-600 text-white px-8 py-4 rounded-xl font-semibold hover:border-cyan-500 hover:bg-cyan-500/10 transition-all duration-300 flex items-center"
              data-testid="hero-cta-secondary"
            >
              Explore Services
              <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="scroll-indicator">
          <div className="scroll-dot"></div>
        </div>
      </div>
    </section>
  );
};

// Service Detail Modal Component
const ServiceDetailModal = ({ service, isOpen, onClose }) => {
  if (!isOpen || !service) return null;

  const IconComponent = iconMap[service.icon] || Shield;

  // Extended service details with comprehensive content
  const serviceDetails = {
    vapt: {
      overview: "Our Vulnerability Assessment and Penetration Testing (VAPT) services provide a comprehensive evaluation of your security posture. We simulate real-world attacks to identify vulnerabilities before malicious actors can exploit them. Our team of certified ethical hackers uses the latest tools and techniques to uncover weaknesses in your systems, applications, and networks.",
      whyChooseUs: [
        "Certified ethical hackers with OSCP, CEH, and CREST certifications",
        "Real-world attack simulation using latest threat intelligence",
        "Zero false positive guarantee with manual verification",
        "Actionable remediation guidance with priority ranking",
        "Free re-testing after vulnerability fixes"
      ],
      methodology: {
        title: "Our VAPT Methodology",
        description: "We follow a structured methodology aligned with OWASP, PTES, and NIST frameworks to ensure comprehensive coverage.",
        phases: [
          { name: "Planning & Scoping", description: "Define scope, rules of engagement, and testing windows to minimize business disruption" },
          { name: "Reconnaissance", description: "Gather intelligence about your systems using passive and active techniques" },
          { name: "Vulnerability Assessment", description: "Identify potential vulnerabilities using automated scanning and manual analysis" },
          { name: "Exploitation", description: "Attempt to exploit identified vulnerabilities to assess real-world impact" },
          { name: "Post-Exploitation", description: "Evaluate the extent of access and potential for lateral movement" },
          { name: "Reporting & Remediation", description: "Deliver detailed findings with business impact and fix recommendations" }
        ]
      },
      testingTypes: [
        { name: "Network Penetration Testing", description: "External and internal network infrastructure assessment" },
        { name: "Web Application Testing", description: "OWASP Top 10 and business logic vulnerability assessment" },
        { name: "Mobile Application Testing", description: "iOS and Android app security assessment" },
        { name: "API Security Testing", description: "REST, GraphQL, and SOAP API vulnerability testing" },
        { name: "Wireless Security Testing", description: "WiFi network and protocol security assessment" },
        { name: "Social Engineering", description: "Phishing, vishing, and physical security testing" }
      ],
      deliverables: [
        "Executive summary with risk score and business impact",
        "Technical findings with proof-of-concept evidence",
        "CVSS-rated vulnerability list with exploitation difficulty",
        "Step-by-step remediation guidance",
        "Compliance mapping (PCI-DSS, ISO 27001, SOC 2)",
        "Re-testing report after fixes"
      ],
      industries: ["Banking & Finance", "Healthcare", "E-commerce", "Government", "Technology", "Insurance"],
      stats: [
        { value: "500+", label: "Assessments Completed" },
        { value: "10K+", label: "Vulnerabilities Found" },
        { value: "99%", label: "Client Satisfaction" },
        { value: "48hrs", label: "Average Turnaround" }
      ]
    },
    appsec: {
      overview: "Our Application Security services ensure your software is built securely from the ground up. We integrate security into your development lifecycle to catch vulnerabilities early and reduce remediation costs by up to 100x compared to production fixes. Our approach combines automated tools with expert manual review.",
      whyChooseUs: [
        "Shift-left security integrated into your CI/CD pipeline",
        "Expert code reviewers with development background",
        "Support for 50+ programming languages and frameworks",
        "DevSecOps transformation consulting",
        "24/7 security monitoring and alerting"
      ],
      methodology: {
        title: "Secure SDLC Framework",
        description: "We embed security at every phase of your software development lifecycle, from design to deployment.",
        phases: [
          { name: "Security Requirements", description: "Define security requirements and threat models during planning" },
          { name: "Secure Design", description: "Review architecture for security flaws and design weaknesses" },
          { name: "Secure Coding", description: "Implement SAST tools and secure coding guidelines" },
          { name: "Security Testing", description: "Automated and manual security testing in CI/CD" },
          { name: "Security Review", description: "Pre-release security sign-off and penetration testing" },
          { name: "Monitoring & Response", description: "Runtime protection and vulnerability management" }
        ]
      },
      testingTypes: [
        { name: "SAST (Static Analysis)", description: "Source code security scanning and code review" },
        { name: "DAST (Dynamic Analysis)", description: "Runtime security testing of running applications" },
        { name: "IAST (Interactive)", description: "Combined static and dynamic analysis with instrumentation" },
        { name: "SCA (Composition Analysis)", description: "Open-source dependency vulnerability scanning" },
        { name: "Container Security", description: "Docker and Kubernetes security scanning" },
        { name: "IaC Security", description: "Terraform, CloudFormation security scanning" }
      ],
      deliverables: [
        "Application threat model documentation",
        "Secure architecture review report",
        "SAST/DAST integration playbook",
        "Secure coding guidelines customized for your stack",
        "Developer security training program",
        "Security metrics and KPI dashboard"
      ],
      industries: ["FinTech", "SaaS Platforms", "Mobile Apps", "Enterprise Software", "Startups", "Healthcare Tech"],
      stats: [
        { value: "100x", label: "Cost Reduction vs Prod Fixes" },
        { value: "85%", label: "Vulnerability Reduction" },
        { value: "50+", label: "Languages Supported" },
        { value: "2hrs", label: "Avg. CI/CD Integration" }
      ]
    },
    grc: {
      overview: "Our Governance, Risk, and Compliance services help you navigate complex regulatory requirements while building a robust security framework aligned with industry standards. We transform compliance from a checkbox exercise into a strategic business advantage that builds customer trust and opens new market opportunities.",
      whyChooseUs: [
        "Certified auditors with Big 4 consulting experience",
        "Support for 20+ compliance frameworks",
        "Automated compliance monitoring and reporting",
        "Audit-ready documentation templates",
        "Ongoing compliance maintenance support"
      ],
      methodology: {
        title: "GRC Implementation Framework",
        description: "Our proven framework helps organizations achieve and maintain compliance efficiently.",
        phases: [
          { name: "Current State Assessment", description: "Evaluate existing controls and identify compliance gaps" },
          { name: "Risk Assessment", description: "Identify, analyze, and prioritize organizational risks" },
          { name: "Framework Selection", description: "Choose and customize appropriate compliance frameworks" },
          { name: "Control Implementation", description: "Design and implement required security controls" },
          { name: "Documentation", description: "Create policies, procedures, and evidence documentation" },
          { name: "Audit Preparation", description: "Mock audits, evidence gathering, and readiness assessment" }
        ]
      },
      testingTypes: [
        { name: "ISO 27001", description: "Information security management system certification" },
        { name: "SOC 2 Type I & II", description: "Trust service criteria audit and attestation" },
        { name: "PCI-DSS", description: "Payment card industry data security standard" },
        { name: "HIPAA", description: "Healthcare data privacy and security compliance" },
        { name: "GDPR", description: "European data protection regulation compliance" },
        { name: "RBI/SEBI Guidelines", description: "Indian financial sector regulatory compliance" }
      ],
      deliverables: [
        "Gap analysis report with remediation roadmap",
        "Risk register with treatment plans",
        "Policy and procedure documentation suite",
        "Control mapping across multiple frameworks",
        "Audit evidence package",
        "Compliance monitoring dashboard"
      ],
      industries: ["Banking & Finance", "Healthcare", "Government", "Manufacturing", "Retail", "Insurance"],
      stats: [
        { value: "100%", label: "Audit Success Rate" },
        { value: "60%", label: "Compliance Cost Reduction" },
        { value: "20+", label: "Frameworks Supported" },
        { value: "3mo", label: "Avg. Time to Compliance" }
      ]
    },
    vciso: {
      overview: "Our Virtual CISO service provides strategic security leadership without the full-time cost. Get expert guidance to build and manage your security program effectively. Our vCISOs bring decades of experience from Fortune 500 companies and provide the same level of expertise at a fraction of the cost of a full-time executive.",
      whyChooseUs: [
        "Former CISOs from Fortune 500 and Big 4 firms",
        "Flexible engagement models (hours, days, or retainer)",
        "Board and investor presentation support",
        "Incident response leadership when you need it",
        "Security team mentoring and development"
      ],
      methodology: {
        title: "Security Program Maturity Model",
        description: "We help organizations progress through security maturity stages with measurable outcomes.",
        phases: [
          { name: "Assessment", description: "Evaluate current security program maturity and capabilities" },
          { name: "Strategy Development", description: "Create a security roadmap aligned with business objectives" },
          { name: "Program Building", description: "Establish security governance, policies, and processes" },
          { name: "Team Development", description: "Build and mentor internal security capabilities" },
          { name: "Metrics & Reporting", description: "Implement security KPIs and executive dashboards" },
          { name: "Continuous Improvement", description: "Regular reviews and program optimization" }
        ]
      },
      testingTypes: [
        { name: "Security Strategy", description: "Multi-year security roadmap development" },
        { name: "Board Advisory", description: "Security presentations and cyber risk reporting" },
        { name: "Vendor Management", description: "Security vendor selection and management" },
        { name: "Incident Leadership", description: "Breach response coordination and communication" },
        { name: "M&A Security", description: "Security due diligence for mergers and acquisitions" },
        { name: "Security Architecture", description: "Enterprise security architecture guidance" }
      ],
      deliverables: [
        "Security strategy and multi-year roadmap",
        "Security program charter and governance framework",
        "Board-ready security presentations",
        "Security budget planning and justification",
        "Vendor risk assessment framework",
        "Incident response and crisis communication plans"
      ],
      industries: ["Startups", "SMBs", "Growing Enterprises", "Non-profits", "Education", "Healthcare"],
      stats: [
        { value: "70%", label: "Cost Savings vs Full-Time" },
        { value: "200+", label: "Companies Advised" },
        { value: "15+", label: "Years Avg. Experience" },
        { value: "24/7", label: "Incident Support" }
      ]
    },
    cloud: {
      overview: "Our Cloud Security services help you secure your cloud infrastructure across AWS, Azure, and GCP. We ensure your cloud deployments are configured securely and compliant with best practices. With organizations moving critical workloads to the cloud, misconfigurations have become the #1 cause of breaches—we help you avoid them.",
      whyChooseUs: [
        "Certified architects across AWS, Azure, and GCP",
        "Real-time cloud security posture monitoring",
        "Infrastructure as Code security scanning",
        "Container and Kubernetes security expertise",
        "Cloud-native security tool implementation"
      ],
      methodology: {
        title: "Cloud Security Assessment Framework",
        description: "Comprehensive evaluation of your cloud environment against CIS benchmarks and cloud provider best practices.",
        phases: [
          { name: "Discovery", description: "Inventory all cloud assets, services, and configurations" },
          { name: "Configuration Review", description: "Assess settings against CIS benchmarks and best practices" },
          { name: "IAM Analysis", description: "Review identity, access, and permission configurations" },
          { name: "Network Security", description: "Evaluate VPCs, security groups, and network architecture" },
          { name: "Data Security", description: "Assess encryption, key management, and data protection" },
          { name: "Logging & Monitoring", description: "Review security monitoring and incident detection" }
        ]
      },
      testingTypes: [
        { name: "CSPM", description: "Cloud Security Posture Management assessment" },
        { name: "IAM Security", description: "Identity and access management review" },
        { name: "Container Security", description: "Docker and Kubernetes security assessment" },
        { name: "Serverless Security", description: "Lambda, Functions, Cloud Run security review" },
        { name: "Data Security", description: "Encryption and data protection assessment" },
        { name: "Network Security", description: "VPC, firewall, and network segmentation review" }
      ],
      deliverables: [
        "Cloud security assessment report",
        "CIS benchmark compliance scorecard",
        "Misconfiguration remediation playbook",
        "IAM least-privilege recommendations",
        "Cloud security architecture design",
        "Continuous monitoring implementation guide"
      ],
      industries: ["Technology", "FinTech", "Healthcare", "Media", "E-commerce", "SaaS"],
      stats: [
        { value: "95%", label: "Misconfiguration Detection" },
        { value: "3", label: "Major Clouds Covered" },
        { value: "200+", label: "Security Checks" },
        { value: "Real-time", label: "Monitoring Setup" }
      ]
    },
    mss: {
      overview: "Our Managed Security Services provide 24/7 security operations and monitoring to detect, respond, and remediate threats in real-time. With the increasing sophistication of cyber attacks, organizations need continuous vigilance—our SOC team becomes an extension of your security capabilities, providing enterprise-grade protection.",
      whyChooseUs: [
        "24/7/365 security monitoring by certified analysts",
        "Advanced threat detection using AI and machine learning",
        "Rapid incident response with defined SLAs",
        "Integrated threat intelligence feeds",
        "Dedicated security analyst assigned to your account"
      ],
      methodology: {
        title: "MSS Operations Framework",
        description: "Our managed security approach combines technology, processes, and expertise for comprehensive protection.",
        phases: [
          { name: "Onboarding & Integration", description: "Deploy monitoring agents and integrate with your infrastructure" },
          { name: "Baseline & Tuning", description: "Establish normal behavior patterns and tune detection rules" },
          { name: "Continuous Monitoring", description: "24/7 monitoring of logs, alerts, and security events" },
          { name: "Threat Detection", description: "Identify potential threats using correlation and analytics" },
          { name: "Incident Response", description: "Rapid containment and remediation of confirmed threats" },
          { name: "Reporting & Optimization", description: "Regular reports and continuous improvement of defenses" }
        ]
      },
      testingTypes: [
        { name: "SIEM Management", description: "Security Information and Event Management" },
        { name: "EDR/XDR", description: "Endpoint Detection and Response monitoring" },
        { name: "Network Monitoring", description: "Network traffic analysis and IDS/IPS management" },
        { name: "Vulnerability Management", description: "Continuous vulnerability scanning and remediation" },
        { name: "Threat Hunting", description: "Proactive threat hunting and investigation" },
        { name: "Incident Response", description: "24/7 incident handling and forensics" }
      ],
      deliverables: [
        "24/7 Security Operations Center coverage",
        "Real-time threat detection and alerting",
        "Monthly security posture reports",
        "Incident response and forensics support",
        "Quarterly business reviews",
        "Compliance reporting and evidence collection"
      ],
      industries: ["Banking & Finance", "Healthcare", "E-commerce", "Manufacturing", "Technology", "Retail"],
      stats: [
        { value: "24/7", label: "SOC Coverage" },
        { value: "<15min", label: "Avg. Response Time" },
        { value: "99.9%", label: "Threat Detection Rate" },
        { value: "1M+", label: "Events Analyzed Daily" }
      ]
    },
    privacy: {
      overview: "Our Privacy Services help organizations navigate the complex landscape of global data protection regulations. From GDPR to India's DPDPA, we provide comprehensive privacy consulting to ensure compliance, build customer trust, and turn privacy into a competitive advantage.",
      whyChooseUs: [
        "Certified privacy professionals (CIPP, CIPM, CIPT)",
        "Experience with global privacy regulations",
        "Privacy by design implementation expertise",
        "Automated privacy compliance tools",
        "Ongoing regulatory change monitoring"
      ],
      methodology: {
        title: "Privacy Program Framework",
        description: "Our structured approach helps organizations build and maintain comprehensive privacy programs.",
        phases: [
          { name: "Privacy Assessment", description: "Evaluate current privacy practices and identify gaps" },
          { name: "Data Discovery", description: "Map personal data flows and create data inventories" },
          { name: "Gap Analysis", description: "Identify compliance gaps against applicable regulations" },
          { name: "Program Design", description: "Design privacy policies, procedures, and controls" },
          { name: "Implementation", description: "Deploy privacy controls and train staff" },
          { name: "Ongoing Compliance", description: "Monitor, audit, and maintain privacy compliance" }
        ]
      },
      testingTypes: [
        { name: "GDPR Compliance", description: "EU General Data Protection Regulation" },
        { name: "DPDPA", description: "India's Digital Personal Data Protection Act" },
        { name: "CCPA/CPRA", description: "California Consumer Privacy Act compliance" },
        { name: "Privacy Impact Assessment", description: "Data Protection Impact Assessments" },
        { name: "Consent Management", description: "Cookie consent and preference management" },
        { name: "Data Subject Rights", description: "DSAR process implementation" }
      ],
      deliverables: [
        "Privacy gap analysis report",
        "Data mapping and inventory documentation",
        "Privacy policy and notice templates",
        "Data Processing Agreements (DPAs)",
        "Privacy impact assessment framework",
        "Staff privacy awareness training"
      ],
      industries: ["Technology", "Healthcare", "E-commerce", "FinTech", "Education", "Media"],
      stats: [
        { value: "100%", label: "Compliance Success" },
        { value: "50+", label: "Privacy Assessments" },
        { value: "6", label: "Regulations Covered" },
        { value: "30days", label: "Avg. Implementation" }
      ]
    }
  };

  const details = serviceDetails[service.id] || serviceDetails.vapt;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="service-detail-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      
      {/* Modal Content */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center">
                <IconComponent className="w-7 h-7 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{service.title}</h2>
                <p className="text-cyan-400 text-sm">{service.shortTitle}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-slate-700 transition-colors"
              data-testid="close-service-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="px-8 py-6 overflow-y-auto max-h-[calc(90vh-120px)] space-y-8">
          {/* Overview */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Overview
            </h3>
            <p className="text-gray-300 leading-relaxed">{details.overview}</p>
          </section>

          {/* Key Features */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Key Features
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {service.features.map((feature, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Our Approach */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Our Approach
            </h3>
            <div className="space-y-3">
              {details.approach.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-cyan-400 text-sm font-bold">{i + 1}</span>
                  </div>
                  <p className="text-gray-300">{step}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Deliverables */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Deliverables
            </h3>
            <ul className="grid sm:grid-cols-2 gap-2">
              {details.deliverables.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                  <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Industries */}
          <section>
            <h3 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center gap-2">
              <Building className="w-5 h-5" />
              Industries We Serve
            </h3>
            <div className="flex flex-wrap gap-2">
              {details.industries.map((industry, i) => (
                <span key={i} className="px-3 py-1.5 bg-slate-800 text-gray-300 text-sm rounded-full border border-slate-700 hover:border-cyan-500/50 transition-colors">
                  {industry}
                </span>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-cyan-500/10 to-blue-600/10 rounded-xl p-6 border border-cyan-500/20">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="text-white font-semibold mb-1">Ready to get started?</h4>
                <p className="text-gray-400 text-sm">Schedule a free consultation to discuss your security needs.</p>
              </div>
              <a
                href="#contact"
                onClick={onClose}
                className="inline-flex items-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg hover:shadow-cyan-500/30 transition-all whitespace-nowrap"
              >
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// Services Section
const ServicesSection = ({ services }) => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="services" className="py-24 bg-slate-800/50 relative overflow-hidden" data-testid="services-section">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-4">
            <span className="text-cyan-400 text-sm font-medium">Our Expertise</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-testid="services-title">
            Comprehensive Security Services
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            From vulnerability assessments to compliance management, we offer end-to-end cybersecurity solutions tailored to your business needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="services-grid">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Shield;
            return (
              <div
                key={service.id}
                className={`group relative bg-slate-900/50 border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-cyan-500/10 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
                data-testid={`service-card-${service.id}`}
              >
                {/* Gradient border on hover */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-sm"></div>
                <div className="absolute inset-[1px] rounded-2xl bg-slate-900 -z-10"></div>
                
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <IconComponent className="w-7 h-7 text-cyan-400 group-hover:text-cyan-300" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">{service.title}</h3>
                <p className="text-gray-400 mb-6 text-sm leading-relaxed">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.slice(0, 4).map((feature, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-300 group-hover:text-gray-200 transition-colors">
                      <CheckCircle className="w-4 h-4 text-cyan-400 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/services/${service.id}`}
                  className="inline-flex items-center text-cyan-400 font-medium text-sm group-hover:text-cyan-300 transition-colors cursor-pointer"
                  data-testid={`learn-more-${service.id}`}
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  
  const features = [
    {
      icon: Target,
      title: "Our Mission",
      description: "Empowering businesses with cutting-edge cybersecurity solutions, safeguarding their digital assets in today's ever-evolving threat landscape."
    },
    {
      icon: Eye,
      title: "Proactive Approach",
      description: "We stay ahead of the curve, continuously adapting to the dynamic landscape of cyber threats and regulatory requirements."
    },
    {
      icon: Award,
      title: "Experienced Leadership",
      description: "Led by industry veterans, our team of highly skilled consultants provides expert guidance to build a resilient security posture."
    },
    {
      icon: Zap,
      title: "Tailored Solutions",
      description: "We deliver unparalleled cybersecurity expertise and guidance, tailored to meet the specific needs of our clients."
    }
  ];

  return (
    <section id="about" className="py-24 bg-slate-900 relative overflow-hidden" data-testid="about-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/10 via-transparent to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={ref} className={`grid lg:grid-cols-2 gap-16 items-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <span className="text-cyan-400 text-sm font-medium">About HTS Consulting</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-testid="about-title">
              Empowering Businesses with
              <span className="gradient-text-animate"> Cybersecurity Excellence</span>
            </h2>
            <p className="text-gray-400 mb-6 leading-relaxed">
              HTS Consulting was founded by a team of seasoned experts with a shared vision of enhancing the security posture of organizations across various industries. Our journey began with the goal of delivering unparalleled cybersecurity expertise and guidance, tailored to meet the specific needs of our clients.
            </p>
            <p className="text-gray-400 mb-8 leading-relaxed">
              At HTS Consulting, our leadership comprises individuals with extensive experience in Cyber Security, regulatory compliance, and managing audits with regulated entities. We are committed to fostering a culture of innovation, integrity, and unwavering dedication to our clients' security needs.
            </p>

            {/* Partner Badges */}
            <div className="flex flex-wrap gap-4" data-testid="about-certifications">
              {["SentinelOne Partner"].map((cert, i) => (
                <div key={i} className="flex items-center px-4 py-2 bg-slate-800 rounded-lg border border-slate-700 hover:border-cyan-500/50 hover:bg-slate-800/80 transition-all duration-300 group">
                  <Award className="w-4 h-4 text-cyan-400 mr-2 group-hover:scale-110 transition-transform" />
                  <span className="text-sm text-gray-300">{cert}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Features Grid */}
          <div className="grid sm:grid-cols-2 gap-6" data-testid="about-features">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-cyan-500/30 hover:-translate-y-1 hover:shadow-lg hover:shadow-cyan-500/5 transition-all duration-300"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 group-hover:scale-110 transition-all duration-300">
                  <feature.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = ({ stats }) => {
  const [ref, isVisible] = useScrollAnimation();
  
  const statIcons = [Users, ShieldCheck, Star, Award, Building];

  return (
    <section ref={ref} className="py-20 relative overflow-hidden" data-testid="stats-section">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-[length:200%_100%] animate-gradient"></div>
      <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}}></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`grid grid-cols-2 md:grid-cols-5 gap-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {stats.map((stat, index) => {
            const Icon = statIcons[index] || Award;
            return (
              <div 
                key={index} 
                className="text-center group"
                style={{ transitionDelay: `${index * 100}ms` }}
                data-testid={`stat-${index}`}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl mb-4 group-hover:bg-white/20 group-hover:scale-110 transition-all duration-300">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                  <AnimatedCounter value={stat.value} />
                </div>
                <div className="text-cyan-100 text-sm font-medium">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Case Studies Section
const CaseStudiesSection = ({ caseStudies }) => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="case-studies" className="py-24 bg-slate-800/50 relative overflow-hidden" data-testid="case-studies-section">
      <div className="absolute top-1/2 left-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-4">
            <span className="text-cyan-400 text-sm font-medium">Success Stories</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-testid="case-studies-title">
            Real Results for Real Businesses
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            See how we've helped organizations across industries strengthen their security posture and achieve compliance.
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="case-studies-grid">
          {caseStudies.map((study, index) => (
            <div
              key={study.id}
              className={`group bg-slate-900/50 border border-slate-700 rounded-2xl overflow-hidden hover:border-cyan-500/50 hover:-translate-y-2 hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
              data-testid={`case-study-${study.id}`}
            >
              {/* Colored top bar */}
              <div className="h-1 bg-gradient-to-r from-cyan-500 to-blue-600 group-hover:h-2 transition-all duration-300"></div>
              
              <div className="p-6">
                <div className="inline-flex items-center px-3 py-1 bg-cyan-500/10 rounded-full mb-4 group-hover:bg-cyan-500/20 transition-colors">
                  <span className="text-cyan-400 text-xs font-medium">{study.industry}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                  {study.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4">{study.challenge}</p>
                
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                    <TrendingUp className="w-4 h-4 text-green-400 mr-2" />
                    Key Results
                  </h4>
                  <ul className="space-y-2">
                    {study.results.map((result, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                        {result}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Clients Section
const ClientsSection = ({ clients }) => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-4">
            <span className="text-cyan-400 text-sm font-medium">Our Clients</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-testid="clients-title">
            Trusted by Industry Leaders
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            We're proud to partner with innovative companies across various industries.
          </p>
        </div>

        {/* Clients Logo Marquee */}
        <div className="relative overflow-hidden">
          <div className="flex animate-marquee space-x-16 items-center">
            {[...clients, ...clients].map((client, index) => {
              // Determine styling based on logo background
              let imgClass = 'h-12 w-auto object-contain hover:opacity-100 transition-all opacity-90';
              
              if (client.name === 'Uni Cards') {
                // Black bg with white text - use screen blend to hide black bg
                imgClass += ' mix-blend-screen';
              } else if (client.name === 'Signzy') {
                // SVG logo - invert to make it white
                imgClass += ' invert';
              }
              
              return (
                <div
                  key={`${client.id}-${index}`}
                  className="flex-shrink-0 flex items-center justify-center h-20 w-48 hover:scale-110 transition-transform duration-300"
                >
                  {client.logo ? (
                    <img 
                      src={client.logo} 
                      alt={client.name}
                      className={imgClass}
                    />
                  ) : (
                    <span className="text-2xl font-bold text-gray-300 hover:text-cyan-400 transition-colors text-center">
                      {client.name}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = ({ team }) => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="team" className="py-24 bg-slate-800/50 relative overflow-hidden" data-testid="team-section">
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-4">
            <span className="text-cyan-400 text-sm font-medium">Our Leadership</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-testid="team-title">
            Meet Our Expert Team
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Industry veterans with decades of combined experience protecting organizations from cyber threats.
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-testid="team-grid">
          {team.map((member, index) => (
            <div
              key={member.id}
              className={`group bg-slate-900/50 border border-slate-700 rounded-2xl p-8 text-center hover:border-cyan-500/50 hover:-translate-y-2 hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
              data-testid={`team-member-${member.id}`}
            >
              {/* Avatar */}
              {member.photo ? (
                <div className="relative w-32 h-32 mx-auto mb-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-cyan-500/50 group-hover:border-cyan-400 transition-colors">
                    <img 
                      src={member.photo} 
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="w-32 h-32 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-4xl font-bold text-white">{member.name.split(' ').map(n => n[0]).join('')}</span>
                </div>
              )}
              
              <h3 className="text-xl font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                {member.name}
              </h3>
              <p className="text-cyan-400 text-sm mb-4 font-medium">{member.role}</p>
              <p className="text-gray-400 text-sm mb-6">{member.bio}</p>
              
              {/* Certifications */}
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                {member.certifications.map((cert, i) => (
                  <span key={i} className="px-3 py-1 bg-slate-800 text-xs text-gray-300 rounded-full border border-slate-700 group-hover:border-cyan-500/30 transition-colors">
                    {cert}
                  </span>
                ))}
              </div>
              
              {/* LinkedIn */}
              <a 
                href={member.linkedin} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center justify-center w-10 h-10 bg-slate-800 rounded-full text-gray-400 hover:text-cyan-400 hover:bg-slate-700 hover:scale-110 transition-all duration-300"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const services = [
    "VAPT Services",
    "Application Security",
    "GRC Services",
    "Virtual CISO",
    "Cloud Security",
    "Security Training",
    "Other"
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Using Web3Forms for contact form submission
      const formDataObj = new FormData();
      formDataObj.append("access_key", "c167b592-b19b-46df-9900-1a9d381f91c8");
      formDataObj.append("subject", `New Contact Form Submission - ${formData.name}`);
      formDataObj.append("from_name", "HTS Consulting Website");
      formDataObj.append("name", formData.name);
      formDataObj.append("email", formData.email);
      formDataObj.append("company", formData.company || "Not provided");
      formDataObj.append("phone", formData.phone || "Not provided");
      formDataObj.append("service", formData.service);
      formDataObj.append("message", formData.message);
      
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formDataObj,
      });
      
      const result = await response.json();
      if (result.success) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", company: "", phone: "", service: "", message: "" });
      } else {
        throw new Error("Form submission failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-slate-900 relative overflow-hidden" data-testid="contact-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={ref} className={`grid lg:grid-cols-2 gap-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Left - Contact Info */}
          <div>
            <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <span className="text-cyan-400 text-sm font-medium">Get In Touch</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-testid="contact-title">
              Ready to Strengthen Your 
              <span className="gradient-text-animate"> Security Posture?</span>
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Schedule a free consultation with our security experts. We'll assess your current security 
              landscape and provide actionable recommendations tailored to your business.
            </p>

            {/* Contact Details */}
            <div className="space-y-6" data-testid="contact-details">
              {[
                { icon: Mail, label: "Email Us", value: "contact@hts.consulting" },
                { icon: Phone, label: "Call Us", value: "+91 96065 75220" },
                { icon: MapPin, label: "Visit Us", value: "Shrinidhi Ascend, No. 30/31, First Floor, Garvebhavi Palya, Hosur Rd, Bommanahalli, Bengaluru, Karnataka 560068" }
              ].map((item, index) => (
                <div key={index} className="flex items-start group">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center mr-4 group-hover:bg-cyan-500/20 group-hover:scale-110 transition-all duration-300">
                    <item.icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{item.label}</div>
                    <div className="text-white font-medium group-hover:text-cyan-400 transition-colors">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="mt-8 flex gap-4" data-testid="social-links">
              <a 
                href="https://www.linkedin.com/company/htsconsultancy/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center text-gray-400 hover:text-cyan-400 hover:bg-slate-700 hover:scale-110 hover:-rotate-6 transition-all duration-300"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl blur-xl opacity-20"></div>
            <div className="relative bg-slate-800/80 backdrop-blur-xl border border-slate-700 rounded-2xl p-8" data-testid="contact-form-container">
              <h3 className="text-xl font-bold text-white mb-6">Send us a message</h3>
              
              {submitStatus === "success" && (
                <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 flex items-center animate-fade-in" data-testid="form-success">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Thank you! We'll get back to you within 24 hours.
                </div>
              )}
              
              {submitStatus === "error" && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 flex items-center animate-fade-in" data-testid="form-error">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Something went wrong. Please try again or email us directly.
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  {[
                    { name: "name", label: "Full Name", type: "text", required: true },
                    { name: "email", label: "Email", type: "email", required: true }
                  ].map((field) => (
                    <div key={field.name} className="relative">
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        onFocus={() => setFocusedField(field.name)}
                        onBlur={() => setFocusedField(null)}
                        required={field.required}
                        className="w-full bg-slate-900/50 border-2 border-slate-600 rounded-xl px-4 py-3 pt-6 text-white placeholder-transparent focus:outline-none focus:border-cyan-500 transition-all duration-300 peer"
                        placeholder={field.label}
                        data-testid={`input-${field.name}`}
                      />
                      <label className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                        formData[field.name] || focusedField === field.name
                          ? 'top-2 text-xs text-cyan-400'
                          : 'top-4 text-sm text-gray-400'
                      }`}>
                        {field.label} {field.required && '*'}
                      </label>
                    </div>
                  ))}
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  {[
                    { name: "company", label: "Company", type: "text" },
                    { name: "phone", label: "Phone", type: "tel" }
                  ].map((field) => (
                    <div key={field.name} className="relative">
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        onFocus={() => setFocusedField(field.name)}
                        onBlur={() => setFocusedField(null)}
                        className="w-full bg-slate-900/50 border-2 border-slate-600 rounded-xl px-4 py-3 pt-6 text-white placeholder-transparent focus:outline-none focus:border-cyan-500 transition-all duration-300 peer"
                        placeholder={field.label}
                        data-testid={`input-${field.name}`}
                      />
                      <label className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                        formData[field.name] || focusedField === field.name
                          ? 'top-2 text-xs text-cyan-400'
                          : 'top-4 text-sm text-gray-400'
                      }`}>
                        {field.label}
                      </label>
                    </div>
                  ))}
                </div>

                <div className="mb-4 relative">
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full bg-slate-900/50 border-2 border-slate-600 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-cyan-500 transition-all duration-300 appearance-none cursor-pointer"
                    data-testid="input-service"
                  >
                    <option value="" className="bg-slate-800">Select a service *</option>
                    {services.map((service, i) => (
                      <option key={i} value={service} className="bg-slate-800">{service}</option>
                    ))}
                  </select>
                  <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 rotate-90 pointer-events-none" />
                </div>

                <div className="mb-6 relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    required
                    rows={4}
                    className="w-full bg-slate-900/50 border-2 border-slate-600 rounded-xl px-4 py-3 pt-6 text-white placeholder-transparent focus:outline-none focus:border-cyan-500 transition-all duration-300 resize-none peer"
                    placeholder="Message"
                    data-testid="input-message"
                  ></textarea>
                  <label className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                    formData.message || focusedField === 'message'
                      ? 'top-2 text-xs text-cyan-400'
                      : 'top-4 text-sm text-gray-400'
                  }`}>
                    Message *
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-xl font-semibold overflow-hidden shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ onTermsClick, onPrivacyClick }) => {
  return (
    <footer className="bg-slate-950 border-t border-slate-800 relative overflow-hidden" data-testid="footer">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-cyan-900/10 via-transparent to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 mb-6 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_5edc397f-12d9-4bf5-9bb6-c4b93ff5263c/artifacts/80m017wg_LLP%20White.png" 
                alt="HTS Consulting" 
                className="h-12 object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
              Your trusted partner in cybersecurity. HTS Consulting by Horizontal Thinkers LLP helps organizations protect their digital assets, 
              achieve compliance, and build resilient security programs.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.linkedin.com/company/htsconsultancy/?viewAsMember=true" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-cyan-400 hover:bg-slate-700 hover:scale-110 hover:-rotate-6 transition-all duration-300"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">Services</h4>
            <ul className="space-y-3">
              {[
                { name: "VAPT Services", path: "/services/vapt" },
                { name: "Application Security", path: "/services/appsec" },
                { name: "GRC Services", path: "/services/grc" },
                { name: "Virtual CISO", path: "/services/vciso" },
                { name: "Cloud Security", path: "/services/cloud" },
                { name: "Managed Security Services", path: "/services/mss" },
                { name: "Privacy", path: "/services/privacy" }
              ].map((service, i) => (
                <li key={i}>
                  <a href={service.path} className="text-gray-400 hover:text-cyan-400 hover:pl-1 transition-all duration-300 text-sm">{service.name}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              {["About Us", "Case Studies", "Careers", "Contact"].map((item, i) => (
                <li key={i}>
                  <a href={`#${item.toLowerCase().replace(' ', '-')}`} className="text-gray-400 hover:text-cyan-400 hover:pl-1 transition-all duration-300 text-sm">{item}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">
            © 2025 Horizontal Thinkers LLP. All rights reserved.
          </p>
          <div className="flex gap-6 mt-4 md:mt-0">
            <button 
              onClick={onPrivacyClick}
              className="text-gray-500 hover:text-cyan-400 text-sm transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </button>
            <button 
              onClick={onTermsClick}
              className="text-gray-500 hover:text-cyan-400 text-sm transition-colors"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Service Detail Page Component
const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  // Use static data instead of API calls
  const services = SERVICES_DATA;
  const service = SERVICES_DATA.find(s => s.id === serviceId);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Service themes with soothing, pleasant colors
  const serviceThemes = {
    vapt: {
      primary: "from-slate-500 to-slate-600",
      primaryHover: "hover:shadow-slate-500/20",
      accent: "text-slate-400",
      accentBg: "bg-slate-500/10",
      accentBorder: "border-slate-500/20",
      iconBg: "from-slate-500 to-slate-600",
      gradientOrb1: "bg-slate-500/5",
      gradientOrb2: "bg-slate-400/5",
      buttonGradient: "from-slate-500 to-slate-600",
      checkColor: "text-slate-400",
      tagBorder: "hover:border-slate-400/30",
      heroImage: "https://static.prod-images.emergentagent.com/jobs/32987f6f-3fd8-4572-8b50-6805bac0a917/images/f76967f929ba107e72b78c8b96a63e399e5902eb23a3143a1bf44927566e7163.png"
    },
    appsec: {
      primary: "from-indigo-400 to-indigo-500",
      primaryHover: "hover:shadow-indigo-400/20",
      accent: "text-indigo-400",
      accentBg: "bg-indigo-400/10",
      accentBorder: "border-indigo-400/20",
      iconBg: "from-indigo-400 to-indigo-500",
      gradientOrb1: "bg-indigo-400/5",
      gradientOrb2: "bg-indigo-300/5",
      buttonGradient: "from-indigo-400 to-indigo-500",
      checkColor: "text-indigo-400",
      tagBorder: "hover:border-indigo-400/30",
      heroImage: "https://static.prod-images.emergentagent.com/jobs/32987f6f-3fd8-4572-8b50-6805bac0a917/images/e124752c4d0b3334846c471fb2329967a80fa5e909a5b747b1b99af5b38d9680.png"
    },
    grc: {
      primary: "from-emerald-400 to-teal-500",
      primaryHover: "hover:shadow-emerald-400/20",
      accent: "text-emerald-400",
      accentBg: "bg-emerald-400/10",
      accentBorder: "border-emerald-400/20",
      iconBg: "from-emerald-400 to-teal-500",
      gradientOrb1: "bg-emerald-400/5",
      gradientOrb2: "bg-teal-400/5",
      buttonGradient: "from-emerald-400 to-teal-500",
      checkColor: "text-emerald-400",
      tagBorder: "hover:border-emerald-400/30",
      heroImage: "https://static.prod-images.emergentagent.com/jobs/e8966f76-de09-4ceb-b586-4acb792319d0/images/39cdc66b73d2489f87c87c5d322d275525d9b2fcfb553787eabb2ba1edfeafb5.png"
    },
    vciso: {
      primary: "from-amber-400 to-orange-400",
      primaryHover: "hover:shadow-amber-400/20",
      accent: "text-amber-400",
      accentBg: "bg-amber-400/10",
      accentBorder: "border-amber-400/20",
      iconBg: "from-amber-400 to-orange-400",
      gradientOrb1: "bg-amber-400/5",
      gradientOrb2: "bg-orange-400/5",
      buttonGradient: "from-amber-400 to-orange-400",
      checkColor: "text-amber-400",
      tagBorder: "hover:border-amber-400/30",
      heroImage: "https://static.prod-images.emergentagent.com/jobs/32987f6f-3fd8-4572-8b50-6805bac0a917/images/bc3fb41fc656faaebee17f2a752e830a6d95a72d039d72bd5385731a915c6b3e.png"
    },
    cloud: {
      primary: "from-sky-400 to-cyan-500",
      primaryHover: "hover:shadow-sky-400/20",
      accent: "text-sky-400",
      accentBg: "bg-sky-400/10",
      accentBorder: "border-sky-400/20",
      iconBg: "from-sky-400 to-cyan-500",
      gradientOrb1: "bg-sky-400/5",
      gradientOrb2: "bg-cyan-400/5",
      buttonGradient: "from-sky-400 to-cyan-500",
      checkColor: "text-sky-400",
      tagBorder: "hover:border-sky-400/30",
      heroImage: "https://static.prod-images.emergentagent.com/jobs/32987f6f-3fd8-4572-8b50-6805bac0a917/images/579e6bca6d59ef8dfb0526b5bbae470f48e37a2ea4c7b52ca99aeb24793f93d3.png"
    },
    mss: {
      primary: "from-teal-400 to-cyan-500",
      primaryHover: "hover:shadow-teal-400/20",
      accent: "text-teal-400",
      accentBg: "bg-teal-400/10",
      accentBorder: "border-teal-400/20",
      iconBg: "from-teal-400 to-cyan-500",
      gradientOrb1: "bg-teal-400/5",
      gradientOrb2: "bg-cyan-400/5",
      buttonGradient: "from-teal-400 to-cyan-500",
      checkColor: "text-teal-400",
      tagBorder: "hover:border-teal-400/30",
      heroImage: "https://static.prod-images.emergentagent.com/jobs/32987f6f-3fd8-4572-8b50-6805bac0a917/images/5f73dd77cf87a2c512f584bac36923b96c42264b1e45efb445e1a07ed7c4c3c6.png"
    },
    privacy: {
      primary: "from-violet-400 to-purple-500",
      primaryHover: "hover:shadow-violet-400/20",
      accent: "text-violet-400",
      accentBg: "bg-violet-400/10",
      accentBorder: "border-violet-400/20",
      iconBg: "from-violet-400 to-purple-500",
      gradientOrb1: "bg-violet-400/5",
      gradientOrb2: "bg-purple-400/5",
      buttonGradient: "from-violet-400 to-purple-500",
      checkColor: "text-violet-400",
      tagBorder: "hover:border-violet-400/30",
      heroImage: "https://static.prod-images.emergentagent.com/jobs/32987f6f-3fd8-4572-8b50-6805bac0a917/images/64f1c7acccf24a15fdbcd68144d524c9a1cc6677b655a2de6b9029fb521cb4df.png"
    }
  };

  // Extended service details with comprehensive content
  const serviceDetails = {
    vapt: {
      overview: "Our Vulnerability Assessment and Penetration Testing (VAPT) services provide a comprehensive evaluation of your security posture. We simulate real-world attacks to identify vulnerabilities before malicious actors can exploit them. Our team of certified ethical hackers uses the latest tools and techniques to uncover weaknesses in your systems, applications, and networks.",
      whyChooseUs: [
        "Certified ethical hackers with OSCP, CEH, and CREST certifications",
        "Real-world attack simulation using latest threat intelligence",
        "Zero false positive guarantee with manual verification",
        "Actionable remediation guidance with priority ranking",
        "Free re-testing after vulnerability fixes"
      ],
      methodology: {
        title: "Our VAPT Methodology",
        description: "We follow a structured methodology aligned with OWASP, PTES, and NIST frameworks to ensure comprehensive coverage.",
        phases: [
          { name: "Planning & Scoping", description: "Define scope, rules of engagement, and testing windows to minimize business disruption" },
          { name: "Reconnaissance", description: "Gather intelligence about your systems using passive and active techniques" },
          { name: "Vulnerability Assessment", description: "Identify potential vulnerabilities using automated scanning and manual analysis" },
          { name: "Exploitation", description: "Attempt to exploit identified vulnerabilities to assess real-world impact" },
          { name: "Post-Exploitation", description: "Evaluate the extent of access and potential for lateral movement" },
          { name: "Reporting & Remediation", description: "Deliver detailed findings with business impact and fix recommendations" }
        ]
      },
      testingTypes: [
        { name: "Network Penetration Testing", description: "External and internal network infrastructure assessment" },
        { name: "Web Application Testing", description: "OWASP Top 10 and business logic vulnerability assessment" },
        { name: "Mobile Application Testing", description: "iOS and Android app security assessment" },
        { name: "API Security Testing", description: "REST, GraphQL, and SOAP API vulnerability testing" },
        { name: "Wireless Security Testing", description: "WiFi network and protocol security assessment" },
        { name: "Social Engineering", description: "Phishing, vishing, and physical security testing" }
      ],
      deliverables: [
        "Executive summary with risk score and business impact",
        "Technical findings with proof-of-concept evidence",
        "CVSS-rated vulnerability list with exploitation difficulty",
        "Step-by-step remediation guidance",
        "Compliance mapping (PCI-DSS, ISO 27001, SOC 2)",
        "Re-testing report after fixes"
      ],
      industries: ["Banking & Finance", "Healthcare", "E-commerce", "Government", "Technology", "Insurance"],
      stats: [
        { value: "500+", label: "Assessments Completed" },
        { value: "10K+", label: "Vulnerabilities Found" },
        { value: "99%", label: "Client Satisfaction" },
        { value: "48hrs", label: "Average Turnaround" }
      ]
    },
    appsec: {
      overview: "Our Application Security services ensure your software is built securely from the ground up. We integrate security into your development lifecycle to catch vulnerabilities early and reduce remediation costs by up to 100x compared to production fixes. Our approach combines automated tools with expert manual review.",
      whyChooseUs: [
        "Shift-left security integrated into your CI/CD pipeline",
        "Expert code reviewers with development background",
        "Support for 50+ programming languages and frameworks",
        "DevSecOps transformation consulting",
        "24/7 security monitoring and alerting"
      ],
      methodology: {
        title: "Secure SDLC Framework",
        description: "We embed security at every phase of your software development lifecycle, from design to deployment.",
        phases: [
          { name: "Security Requirements", description: "Define security requirements and threat models during planning" },
          { name: "Secure Design", description: "Review architecture for security flaws and design weaknesses" },
          { name: "Secure Coding", description: "Implement SAST tools and secure coding guidelines" },
          { name: "Security Testing", description: "Automated and manual security testing in CI/CD" },
          { name: "Security Review", description: "Pre-release security sign-off and penetration testing" },
          { name: "Monitoring & Response", description: "Runtime protection and vulnerability management" }
        ]
      },
      testingTypes: [
        { name: "SAST (Static Analysis)", description: "Source code security scanning and code review" },
        { name: "DAST (Dynamic Analysis)", description: "Runtime security testing of running applications" },
        { name: "IAST (Interactive)", description: "Combined static and dynamic analysis with instrumentation" },
        { name: "SCA (Composition Analysis)", description: "Open-source dependency vulnerability scanning" },
        { name: "Container Security", description: "Docker and Kubernetes security scanning" },
        { name: "IaC Security", description: "Terraform, CloudFormation security scanning" }
      ],
      deliverables: [
        "Application threat model documentation",
        "Secure architecture review report",
        "SAST/DAST integration playbook",
        "Secure coding guidelines customized for your stack",
        "Developer security training program",
        "Security metrics and KPI dashboard"
      ],
      industries: ["FinTech", "SaaS Platforms", "Mobile Apps", "Enterprise Software", "Startups", "Healthcare Tech"],
      stats: [
        { value: "100x", label: "Cost Reduction vs Prod Fixes" },
        { value: "85%", label: "Vulnerability Reduction" },
        { value: "50+", label: "Languages Supported" },
        { value: "2hrs", label: "Avg. CI/CD Integration" }
      ]
    },
    grc: {
      overview: "Our Governance, Risk, and Compliance services help you navigate complex regulatory requirements while building a robust security framework aligned with industry standards. We transform compliance from a checkbox exercise into a strategic business advantage that builds customer trust and opens new market opportunities.",
      whyChooseUs: [
        "Certified auditors with Big 4 consulting experience",
        "Support for 20+ compliance frameworks",
        "Automated compliance monitoring and reporting",
        "Audit-ready documentation templates",
        "Ongoing compliance maintenance support"
      ],
      methodology: {
        title: "GRC Implementation Framework",
        description: "Our proven framework helps organizations achieve and maintain compliance efficiently.",
        phases: [
          { name: "Current State Assessment", description: "Evaluate existing controls and identify compliance gaps" },
          { name: "Risk Assessment", description: "Identify, analyze, and prioritize organizational risks" },
          { name: "Framework Selection", description: "Choose and customize appropriate compliance frameworks" },
          { name: "Control Implementation", description: "Design and implement required security controls" },
          { name: "Documentation", description: "Create policies, procedures, and evidence documentation" },
          { name: "Audit Preparation", description: "Mock audits, evidence gathering, and readiness assessment" }
        ]
      },
      testingTypes: [
        { name: "ISO 27001", description: "Information security management system certification" },
        { name: "SOC 2 Type I & II", description: "Trust service criteria audit and attestation" },
        { name: "PCI-DSS", description: "Payment card industry data security standard" },
        { name: "HIPAA", description: "Healthcare data privacy and security compliance" },
        { name: "GDPR", description: "European data protection regulation compliance" },
        { name: "RBI/SEBI Guidelines", description: "Indian financial sector regulatory compliance" }
      ],
      deliverables: [
        "Gap analysis report with remediation roadmap",
        "Risk register with treatment plans",
        "Policy and procedure documentation suite",
        "Control mapping across multiple frameworks",
        "Audit evidence package",
        "Compliance monitoring dashboard"
      ],
      industries: ["Banking & Finance", "Healthcare", "Government", "Manufacturing", "Retail", "Insurance"],
      stats: [
        { value: "100%", label: "Audit Success Rate" },
        { value: "60%", label: "Compliance Cost Reduction" },
        { value: "20+", label: "Frameworks Supported" },
        { value: "3mo", label: "Avg. Time to Compliance" }
      ]
    },
    vciso: {
      overview: "Our Virtual CISO service provides strategic security leadership without the full-time cost. Get expert guidance to build and manage your security program effectively. Our vCISOs bring decades of experience from Fortune 500 companies and provide the same level of expertise at a fraction of the cost of a full-time executive.",
      whyChooseUs: [
        "Former CISOs from Fortune 500 and Big 4 firms",
        "Flexible engagement models (hours, days, or retainer)",
        "Board and investor presentation support",
        "Incident response leadership when you need it",
        "Security team mentoring and development"
      ],
      methodology: {
        title: "Security Program Maturity Model",
        description: "We help organizations progress through security maturity stages with measurable outcomes.",
        phases: [
          { name: "Assessment", description: "Evaluate current security program maturity and capabilities" },
          { name: "Strategy Development", description: "Create a security roadmap aligned with business objectives" },
          { name: "Program Building", description: "Establish security governance, policies, and processes" },
          { name: "Team Development", description: "Build and mentor internal security capabilities" },
          { name: "Metrics & Reporting", description: "Implement security KPIs and executive dashboards" },
          { name: "Continuous Improvement", description: "Regular reviews and program optimization" }
        ]
      },
      testingTypes: [
        { name: "Security Strategy", description: "Multi-year security roadmap development" },
        { name: "Board Advisory", description: "Security presentations and cyber risk reporting" },
        { name: "Vendor Management", description: "Security vendor selection and management" },
        { name: "Incident Leadership", description: "Breach response coordination and communication" },
        { name: "M&A Security", description: "Security due diligence for mergers and acquisitions" },
        { name: "Security Architecture", description: "Enterprise security architecture guidance" }
      ],
      deliverables: [
        "Security strategy and multi-year roadmap",
        "Security program charter and governance framework",
        "Board-ready security presentations",
        "Security budget planning and justification",
        "Vendor risk assessment framework",
        "Incident response and crisis communication plans"
      ],
      industries: ["Startups", "SMBs", "Growing Enterprises", "Non-profits", "Education", "Healthcare"],
      stats: [
        { value: "70%", label: "Cost Savings vs Full-Time" },
        { value: "200+", label: "Companies Advised" },
        { value: "15+", label: "Years Avg. Experience" },
        { value: "24/7", label: "Incident Support" }
      ]
    },
    cloud: {
      overview: "Our Cloud Security services help you secure your cloud infrastructure across AWS, Azure, and GCP. We ensure your cloud deployments are configured securely and compliant with best practices. With organizations moving critical workloads to the cloud, misconfigurations have become the #1 cause of breaches—we help you avoid them.",
      whyChooseUs: [
        "Certified architects across AWS, Azure, and GCP",
        "Real-time cloud security posture monitoring",
        "Infrastructure as Code security scanning",
        "Container and Kubernetes security expertise",
        "Cloud-native security tool implementation"
      ],
      methodology: {
        title: "Cloud Security Assessment Framework",
        description: "Comprehensive evaluation of your cloud environment against CIS benchmarks and cloud provider best practices.",
        phases: [
          { name: "Discovery", description: "Inventory all cloud assets, services, and configurations" },
          { name: "Configuration Review", description: "Assess settings against CIS benchmarks and best practices" },
          { name: "IAM Analysis", description: "Review identity, access, and permission configurations" },
          { name: "Network Security", description: "Evaluate VPCs, security groups, and network architecture" },
          { name: "Data Security", description: "Assess encryption, key management, and data protection" },
          { name: "Logging & Monitoring", description: "Review security monitoring and incident detection" }
        ]
      },
      testingTypes: [
        { name: "CSPM", description: "Cloud Security Posture Management assessment" },
        { name: "IAM Security", description: "Identity and access management review" },
        { name: "Container Security", description: "Docker and Kubernetes security assessment" },
        { name: "Serverless Security", description: "Lambda, Functions, Cloud Run security review" },
        { name: "Data Security", description: "Encryption and data protection assessment" },
        { name: "Network Security", description: "VPC, firewall, and network segmentation review" }
      ],
      deliverables: [
        "Cloud security assessment report",
        "CIS benchmark compliance scorecard",
        "Misconfiguration remediation playbook",
        "IAM least-privilege recommendations",
        "Cloud security architecture design",
        "Continuous monitoring implementation guide"
      ],
      industries: ["Technology", "FinTech", "Healthcare", "Media", "E-commerce", "SaaS"],
      stats: [
        { value: "95%", label: "Misconfiguration Detection" },
        { value: "3", label: "Major Clouds Covered" },
        { value: "200+", label: "Security Checks" },
        { value: "Real-time", label: "Monitoring Setup" }
      ]
    },
    mss: {
      overview: "Our Managed Security Services provide 24/7 security operations and monitoring to detect, respond, and remediate threats in real-time. With the increasing sophistication of cyber attacks, organizations need continuous vigilance—our SOC team becomes an extension of your security capabilities, providing enterprise-grade protection.",
      whyChooseUs: [
        "24/7/365 security monitoring by certified analysts",
        "Advanced threat detection using AI and machine learning",
        "Rapid incident response with defined SLAs",
        "Integrated threat intelligence feeds",
        "Dedicated security analyst assigned to your account"
      ],
      methodology: {
        title: "MSS Operations Framework",
        description: "Our managed security approach combines technology, processes, and expertise for comprehensive protection.",
        phases: [
          { name: "Onboarding & Integration", description: "Deploy monitoring agents and integrate with your infrastructure" },
          { name: "Baseline & Tuning", description: "Establish normal behavior patterns and tune detection rules" },
          { name: "Continuous Monitoring", description: "24/7 monitoring of logs, alerts, and security events" },
          { name: "Threat Detection", description: "Identify potential threats using correlation and analytics" },
          { name: "Incident Response", description: "Rapid containment and remediation of confirmed threats" },
          { name: "Reporting & Optimization", description: "Regular reports and continuous improvement of defenses" }
        ]
      },
      testingTypes: [
        { name: "SIEM Management", description: "Security Information and Event Management" },
        { name: "EDR/XDR", description: "Endpoint Detection and Response monitoring" },
        { name: "Network Monitoring", description: "Network traffic analysis and IDS/IPS management" },
        { name: "Vulnerability Management", description: "Continuous vulnerability scanning and remediation" },
        { name: "Threat Hunting", description: "Proactive threat hunting and investigation" },
        { name: "Incident Response", description: "24/7 incident handling and forensics" }
      ],
      deliverables: [
        "24/7 Security Operations Center coverage",
        "Real-time threat detection and alerting",
        "Monthly security posture reports",
        "Incident response and forensics support",
        "Quarterly business reviews",
        "Compliance reporting and evidence collection"
      ],
      industries: ["Banking & Finance", "Healthcare", "E-commerce", "Manufacturing", "Technology", "Retail"],
      stats: [
        { value: "24/7", label: "SOC Coverage" },
        { value: "<15min", label: "Avg. Response Time" },
        { value: "99.9%", label: "Threat Detection Rate" },
        { value: "1M+", label: "Events Analyzed Daily" }
      ]
    },
    privacy: {
      overview: "Our Privacy Services help organizations navigate the complex landscape of global data protection regulations. From GDPR to India's DPDPA, we provide comprehensive privacy consulting to ensure compliance, build customer trust, and turn privacy into a competitive advantage.",
      whyChooseUs: [
        "Certified privacy professionals (CIPP, CIPM, CIPT)",
        "Experience with global privacy regulations",
        "Privacy by design implementation expertise",
        "Automated privacy compliance tools",
        "Ongoing regulatory change monitoring"
      ],
      methodology: {
        title: "Privacy Program Framework",
        description: "Our structured approach helps organizations build and maintain comprehensive privacy programs.",
        phases: [
          { name: "Privacy Assessment", description: "Evaluate current privacy practices and identify gaps" },
          { name: "Data Discovery", description: "Map personal data flows and create data inventories" },
          { name: "Gap Analysis", description: "Identify compliance gaps against applicable regulations" },
          { name: "Program Design", description: "Design privacy policies, procedures, and controls" },
          { name: "Implementation", description: "Deploy privacy controls and train staff" },
          { name: "Ongoing Compliance", description: "Monitor, audit, and maintain privacy compliance" }
        ]
      },
      testingTypes: [
        { name: "GDPR Compliance", description: "EU General Data Protection Regulation" },
        { name: "DPDPA", description: "India's Digital Personal Data Protection Act" },
        { name: "CCPA/CPRA", description: "California Consumer Privacy Act compliance" },
        { name: "Privacy Impact Assessment", description: "Data Protection Impact Assessments" },
        { name: "Consent Management", description: "Cookie consent and preference management" },
        { name: "Data Subject Rights", description: "DSAR process implementation" }
      ],
      deliverables: [
        "Privacy gap analysis report",
        "Data mapping and inventory documentation",
        "Privacy policy and notice templates",
        "Data Processing Agreements (DPAs)",
        "Privacy impact assessment framework",
        "Staff privacy awareness training"
      ],
      industries: ["Technology", "Healthcare", "E-commerce", "FinTech", "Education", "Media"],
      stats: [
        { value: "100%", label: "Compliance Success" },
        { value: "50+", label: "Privacy Assessments" },
        { value: "6", label: "Regulations Covered" },
        { value: "30days", label: "Avg. Implementation" }
      ]
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Redirect if service not found
    if (!service) {
      navigate('/');
    }
    window.scrollTo(0, 0);
  }, [serviceId, navigate, service]);

  if (!service) {
    return null;
  }

  const details = serviceDetails[service.id] || serviceDetails.vapt;
  const theme = serviceThemes[service.id] || serviceThemes.vapt;
  const IconComponent = iconMap[service.icon] || Shield;

  return (
    <div className="App bg-slate-900 min-h-screen">
      <Navbar isScrolled={isScrolled} services={services} />
      
      {/* Hero Section with Full-Width Image */}
      <section className="relative pt-20 overflow-hidden">
        {/* Hero Image Background */}
        <div className="absolute inset-0 h-[500px]">
          <img 
            src={theme.heroImage} 
            alt={service.title}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/90 to-slate-900"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/50 via-transparent to-slate-900/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-32">
          {/* Back Button */}
          <Link 
            to="/#services" 
            className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors group"
          >
            <ArrowRight className="w-4 h-4 mr-2 rotate-180 group-hover:-translate-x-1 transition-transform" />
            Back to Services
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={`inline-flex items-center px-4 py-2 ${theme.accentBg} border ${theme.accentBorder} rounded-full mb-6`}>
                <span className={`${theme.accent} text-sm font-medium`}>{service.shortTitle}</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">{service.title}</h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">{service.description}</p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#get-in-touch"
                  className={`inline-flex items-center bg-gradient-to-r ${theme.buttonGradient} text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg ${theme.primaryHover} transition-all`}
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
                <a
                  href="#methodology"
                  className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/20"
                >
                  Learn More
                </a>
              </div>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              {details.stats?.map((stat, i) => (
                <div key={i} className={`bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 ${i === 0 ? 'col-span-2' : ''}`}>
                  <div className={`text-3xl md:text-4xl font-bold ${theme.accent} mb-1`}>{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-slate-800/30 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Key Features</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Everything you need for comprehensive {service.shortTitle} coverage</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((feature, i) => (
              <div key={i} className={`group bg-slate-900/50 border border-slate-700 rounded-2xl p-6 ${theme.tagBorder} transition-all hover:-translate-y-1`}>
                <div className={`w-12 h-12 bg-gradient-to-br ${theme.iconBg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      {details.whyChooseUs && (
        <section className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Why Choose HTS Consulting?</h2>
                <p className="text-gray-400 mb-8">We bring industry-leading expertise and proven methodologies to every engagement.</p>
                <div className="space-y-4">
                  {details.whyChooseUs.map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className={`w-8 h-8 bg-gradient-to-br ${theme.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-gray-300 pt-1">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradientOrb1} rounded-3xl blur-3xl opacity-30`}></div>
                <img 
                  src={theme.heroImage} 
                  alt={service.title}
                  className="relative rounded-2xl border border-slate-700 shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Methodology Section */}
      {details.methodology && (
        <section id="methodology" className="py-20 bg-slate-800/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{details.methodology.title}</h2>
              <p className="text-gray-400 max-w-2xl mx-auto">{details.methodology.description}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {details.methodology.phases.map((phase, i) => (
                <div key={i} className="relative bg-slate-900/50 border border-slate-700 rounded-2xl p-6 hover:border-slate-600 transition-all">
                  <div className={`absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br ${theme.iconBg} rounded-xl flex items-center justify-center shadow-lg`}>
                    <span className="text-white font-bold">{i + 1}</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2 mt-2">{phase.name}</h3>
                  <p className="text-gray-400 text-sm">{phase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services/Testing Types Section */}
      {details.testingTypes && (
        <section className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {service.id === 'grc' ? 'Compliance Frameworks' : service.id === 'vciso' ? 'Advisory Services' : service.id === 'privacy' ? 'Regulations We Cover' : 'Our Services'}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {details.testingTypes.map((type, i) => (
                <div key={i} className={`group bg-slate-800/50 border border-slate-700 rounded-2xl p-6 ${theme.tagBorder} transition-all hover:-translate-y-1`}>
                  <div className={`w-12 h-12 ${theme.accentBg} rounded-xl flex items-center justify-center mb-4`}>
                    <Server className={`w-6 h-6 ${theme.accent}`} />
                  </div>
                  <h3 className={`font-semibold text-white mb-2 group-hover:${theme.accent} transition-colors`}>{type.name}</h3>
                  <p className="text-gray-400 text-sm">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Deliverables Section */}
      <section className="py-20 bg-slate-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">What You'll Receive</h2>
              <p className="text-gray-400 mb-8">Comprehensive deliverables designed to provide actionable insights and long-term value.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {details.deliverables.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`w-6 h-6 ${theme.accentBg} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <ArrowRight className={`w-4 h-4 ${theme.accent}`} />
                    </div>
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={`bg-gradient-to-br ${theme.accentBg} border ${theme.accentBorder} rounded-2xl p-8`}>
              <h3 className="text-2xl font-bold text-white mb-4">Industries We Serve</h3>
              <div className="flex flex-wrap gap-3">
                {details.industries.map((industry, i) => (
                  <span key={i} className={`px-4 py-2 bg-slate-900/50 text-gray-300 rounded-full border border-slate-700 ${theme.tagBorder} transition-colors`}>
                    {industry}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get in Touch Section */}
      <section id="get-in-touch" className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Get in Touch</h2>
            <p className="text-gray-400">Interested in {service.title}? Let's discuss how we can help secure your business.</p>
          </div>
          <ServiceContactForm serviceName={service.title} theme={theme} />
        </div>
      </section>

      <Footer onTermsClick={() => setShowTerms(true)} onPrivacyClick={() => setShowPrivacy(true)} />
      <BackToTop />
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyPolicyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  );
};

// Service Contact Form Component
const ServiceContactForm = ({ serviceName, theme }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: serviceName,
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Using Web3Forms for contact form submission
      const formDataObj = new FormData();
      formDataObj.append("access_key", "c167b592-b19b-46df-9900-1a9d381f91c8");
      formDataObj.append("subject", `New ${serviceName} Inquiry - ${formData.name}`);
      formDataObj.append("from_name", "HTS Consulting Website");
      formDataObj.append("name", formData.name);
      formDataObj.append("email", formData.email);
      formDataObj.append("company", formData.company || "Not provided");
      formDataObj.append("phone", formData.phone || "Not provided");
      formDataObj.append("service", formData.service);
      formDataObj.append("message", formData.message);
      
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formDataObj,
      });
      
      const result = await response.json();
      if (result.success) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", company: "", phone: "", service: serviceName, message: "" });
      } else {
        throw new Error("Form submission failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`bg-slate-800/80 backdrop-blur-xl border border-slate-700 rounded-2xl p-8`}>
      {submitStatus === "success" && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          Thank you! We'll get back to you within 24 hours.
        </div>
      )}
      
      {submitStatus === "error" && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2" />
          Something went wrong. Please try again or email us directly.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          {[
            { name: "name", label: "Full Name", type: "text", required: true },
            { name: "email", label: "Email", type: "email", required: true }
          ].map((field) => (
            <div key={field.name} className="relative">
              <input
                type={field.type}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                onFocus={() => setFocusedField(field.name)}
                onBlur={() => setFocusedField(null)}
                required={field.required}
                className={`w-full bg-slate-900/50 border-2 border-slate-600 rounded-xl px-4 py-3 pt-6 text-white placeholder-transparent focus:outline-none transition-all duration-300 ${focusedField === field.name ? theme.accentBorder.replace('border-', 'border-').replace('/30', '') : ''}`}
                placeholder={field.label}
              />
              <label className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                formData[field.name] || focusedField === field.name
                  ? `top-2 text-xs ${theme.accent}`
                  : 'top-4 text-sm text-gray-400'
              }`}>
                {field.label} {field.required && '*'}
              </label>
            </div>
          ))}
        </div>
        
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          {[
            { name: "company", label: "Company", type: "text" },
            { name: "phone", label: "Phone", type: "tel" }
          ].map((field) => (
            <div key={field.name} className="relative">
              <input
                type={field.type}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                onFocus={() => setFocusedField(field.name)}
                onBlur={() => setFocusedField(null)}
                className={`w-full bg-slate-900/50 border-2 border-slate-600 rounded-xl px-4 py-3 pt-6 text-white placeholder-transparent focus:outline-none transition-all duration-300 ${focusedField === field.name ? theme.accentBorder.replace('border-', 'border-').replace('/30', '') : ''}`}
                placeholder={field.label}
              />
              <label className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                formData[field.name] || focusedField === field.name
                  ? `top-2 text-xs ${theme.accent}`
                  : 'top-4 text-sm text-gray-400'
              }`}>
                {field.label}
              </label>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <div className={`w-full bg-slate-900/50 border-2 border-slate-600 rounded-xl px-4 py-4 ${theme.accent}`}>
            Service: {serviceName}
          </div>
        </div>

        <div className="mb-6 relative">
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            onFocus={() => setFocusedField('message')}
            onBlur={() => setFocusedField(null)}
            required
            rows={4}
            className={`w-full bg-slate-900/50 border-2 border-slate-600 rounded-xl px-4 py-3 pt-6 text-white placeholder-transparent focus:outline-none transition-all duration-300 resize-none ${focusedField === 'message' ? theme.accentBorder.replace('border-', 'border-').replace('/30', '') : ''}`}
            placeholder="Message"
          ></textarea>
          <label className={`absolute left-4 transition-all duration-300 pointer-events-none ${
            formData.message || focusedField === 'message'
              ? `top-2 text-xs ${theme.accent}`
              : 'top-4 text-sm text-gray-400'
          }`}>
            Message *
          </label>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full bg-gradient-to-r ${theme.buttonGradient} text-white py-4 rounded-xl font-semibold hover:shadow-lg ${theme.primaryHover} transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              Send Message
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </button>
      </form>
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Use static data directly
  const services = SERVICES_DATA;
  const stats = STATS_DATA;
  const clients = CLIENTS_DATA;
  const caseStudies = CASE_STUDIES_DATA;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App bg-slate-900 min-h-screen" data-testid="app">
      <Navbar isScrolled={isScrolled} services={services} />
      <HeroSection />
      <ServicesSection services={services} />
      <AboutSection />
      <StatsSection stats={stats} />
      <CaseStudiesSection caseStudies={caseStudies} />
      <ClientsSection clients={clients} />
      <ContactSection />
      <Footer onTermsClick={() => setShowTerms(true)} onPrivacyClick={() => setShowPrivacy(true)} />
      <BackToTop />
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyPolicyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  );
};

// Main App Component with Router
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
      </Routes>
    </Router>
  );
}

export default App;
